import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { SubmissionView } from '../models/submission-view.model';
import {
  PanelBodyComponent,
  PanelContainerComponent,
  PanelHeaderComponent,
} from 'src/app/shared/content-panels';
import { MatIconModule, MatTooltipModule } from 'src/app/shared/material';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  DynamicForm,
  DynamicFormControl,
  DynamicFormGroup,
  ControlType,
  DynamicFormGroupComponent,
  FormBuilderService,
  FormRule,
  FormRuleEffect,
  RuleAction,
  DynamicControlValidatorRegistry,
} from 'src/app/shared/forms';
import { homeCenterValidator } from './home-center.validator';
import { facilityValidator } from './facility.validator';
import { SubmissionsService } from '../services/submissions.service';
import { forkJoin, Observable, of, Subject, switchMap } from 'rxjs';
import { Submission } from '../models';
import { CustomFormRulesService } from '../services/custom-form-rules.service';
import {
  AlertActions,
  SpinnerService,
  TokenClaimsService,
} from 'src/app/shared/services';
import { Subscription } from 'rxjs';
import { SubmissionConstants } from '../models/constants/submissions-constants';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { UpdateResult } from 'src/app/shared/graph-queries/types';

@Component({
  selector: 'app-submissions-detail-view',
  providers: [DynamicControlValidatorRegistry],
  imports: [
    PanelContainerComponent,
    PanelHeaderComponent,
    PanelBodyComponent,
    ReactiveFormsModule,
    DynamicFormGroupComponent,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './submissions-detail-view.component.html',
  styleUrl: './submissions-detail-view.component.scss',
})
export class SubmissionsDetailViewComponent implements OnChanges {
  @Input() submissionViewItem: SubmissionView;
  @Output() close = new EventEmitter();
  @Output() save = new EventEmitter();
  @Output() statusUpdate = new EventEmitter<string>();
  @Output() delete = new EventEmitter();
  @Output() unsavedChangesUpdate = new EventEmitter<boolean>();

  submission: Submission;
  form: FormGroup;
  dynamicForm: DynamicForm;
  FormStructureId: string;
  heading: string;
  shouldShowDeleteButton: boolean = false;
  shouldShowResetStatusButton: boolean = false;
  shouldShowConvertV3ToV2Button: boolean = false;
  shouldShowConvertV2ToV3Button: boolean = false;

  subStatus: string = '';
  canShowManualSubmittedDate: boolean = false;
  shouldSaveBeDisabled: boolean = false;
  private isHydratingForm: boolean = false;
  private isConverting: boolean = false;

  private _subscriptions: Subscription[] = [];
  onChanges = new Subject<SimpleChanges>();
  formSaveBanner: string;

  constructor(
    public formService: FormBuilderService,
    public subService: SubmissionsService,
    private formBuilder: FormBuilder,
    private customRulesService: CustomFormRulesService,
    private spinnerService: SpinnerService,
    private submissionsConst: SubmissionConstants,
    private claimsService: TokenClaimsService,
    public dialog: MatDialog,
    public dialogService: DialogService,
    private validatorRegistry: DynamicControlValidatorRegistry,
  ) {
    this.validatorRegistry.register('homeCenter', homeCenterValidator());
    this.validatorRegistry.register('facility', facilityValidator());
  }

  ngOnChanges(changes: SimpleChanges) {
    this.onChanges.next(changes);
  }

  ngOnInit() {
    this.submissionFormSetup();
    // Clear previous form subscriptions before populating updated submission form
    this.onChanges.subscribe((data: SimpleChanges) => {
      if (data['submissionViewItem']) {
        this.dynamicForm = null;
        this.formService.clearRuleHashBlackList();
        this._subscriptions.forEach((i) => i.unsubscribe());
        this.submissionFormSetup();
      }
    });
  }
  calculateShouldShowDeleteButton(): void {
    const isSubmitted = this.submissionsConst.submittedStatuses.includes(
      this.subStatus,
    );
    this.shouldShowDeleteButton =
      this.claimsService.isEnergyCreditAdmin && !isSubmitted;
  }
  calculateShouldShowResetStatusButton(): void {
    const isSubmitted =
      this.subStatus === this.submissionsConst.statusSubmitted ||
      this.subStatus === this.submissionsConst.statusSubmittedManually;
    this.shouldShowResetStatusButton =
      this.claimsService.isEnergyCreditAdmin && isSubmitted;
  }

  calculateShouldShowConvertV3ToV2Button(): void {
    const isSubmitted = this.submissionsConst.submittedStatuses.includes(
      this.subStatus,
    );
    this.shouldShowConvertV3ToV2Button =
      this.claimsService.isEnergyCreditAdmin &&
      !isSubmitted &&
      !!this.submission &&
      Submission.IsSbraV3(this.submission);
  }

  calculateShouldShowConvertV2ToV3Button(): void {
    const isSubmitted = this.submissionsConst.submittedStatuses.includes(
      this.subStatus,
    );
    this.shouldShowConvertV2ToV3Button =
      this.claimsService.isEnergyCreditAdmin &&
      !isSubmitted &&
      !!this.submission &&
      Submission.IsSbraV2(this.submission);
  }

  ngOnDestroy(): void {
    this.formService.clearRuleHashBlackList();
    this._subscriptions.forEach((i) => i.unsubscribe());
  }

  saveChanges() {
    this.spinnerService.show();
    this.subService.saveSubmission(this.submission, this.form).subscribe({
      next: (value: any) => {
        const data: UpdateResult = value.data.UpdateEnergyCreditSubmission;
        let savedStatus = this.subStatus;
        if (data.Success && data.UpdatedData) {
          this.submission = this.toSubmission(data.UpdatedData);
          this.canShowManualSubmittedDate = this.isSavedManualSubmission();
          savedStatus = this.getStatus(this.submission) ?? savedStatus;
          this.subStatus = savedStatus;
        }
        if (
          (Submission.IsSbraV2(this.submission) &&
            savedStatus === this.submissionsConst.statusSubmitted) ||
          !data.Success
        ) {
          this.showSbraV2Messaging(data);
        }
        // Lock controls after successful save if status is submitted
        if (this.submissionsConst.submittedStatuses.includes(savedStatus)) {
          this.lockControls(this.dynamicForm.getControls());
        }
        if (
          Submission.IsSbraV3(this.submission) &&
          this.getStatus(this.submission) ===
            this.submissionsConst.statusReadyToSubmit.toString()
        ) {
          this.displayFormBanner(this.submissionsConst.bannerSbraV3Success);
        }
        if (
          Submission.IsNEEMPlant(this.getFacilityNumber(this.submission)) &&
          this.getStatus(this.submission) ===
            this.submissionsConst.statusReadyToSubmit.toString()
        ) {
          this.displayFormBanner(this.submissionsConst.bannerIsNeemSuccess);
        }
        if (
          this.getStatus(this.submission) !==
            this.submissionsConst.statusReadyToSubmit.toString() &&
          data.Success &&
          !(
            Submission.IsSbraV2(this.submission) &&
            savedStatus === this.submissionsConst.statusSubmitted
          )
        ) {
          this.displayFormBanner(
            this.submissionsConst.bannerGeneralStatusSuccess,
          );
        }
        this.spinnerService.hide();
        this.runRuleSet(this.form);
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.unsavedChangesUpdate.emit(false);
        this.save.emit();
        this.updateButtonVisibilities();
      },
      error: (err) => {
        console.log('failed to save submission', err);
        this.dialogService.openDialog(
          '',
          this.submissionsConst.subSbraGenericFailMessage,
          this.submissionsConst.subSbraButtonTxtCancel,
          this.submissionsConst.subSbraButtonTxtConfirm,
          this.submissionsConst.subSbraShowCancelButton,
        );
        this.spinnerService.hide();
      },
    });
  }

  private updateButtonVisibilities() {
    this.calculateShouldShowResetStatusButton();
    this.calculateShouldShowDeleteButton();
    this.calculateShouldShowConvertV3ToV2Button();
    this.calculateShouldShowConvertV2ToV3Button();
  }

  private getFacilityNumber(sub: Submission): string | undefined {
    return sub.SubmissionValues.find(
      (x) => x.PropertyBindingKey === 'facilityNumber',
    )?.Value;
  }

  private getStatus(sub: Submission): string {
    return sub.SubmissionValues.find((x) => x.PropertyBindingKey === 'status')
      ?.Value;
  }

  private showSbraV2Messaging(data: UpdateResult): void {
    if (data.Success) {
      this.displayFormBanner(this.submissionsConst.bannerSbraV2Success);
      this.subService.getSubmission(this.submissionViewItem.Id);
      return;
    }
    if (data.Message.startsWith(AlertActions.SBRA_SUBMISSION_ERROR)) {
      const title = (data.Message ?? '').includes('record already exists')
        ? this.submissionsConst.subSbraFailDuplicateTitle
        : this.submissionsConst.subSbraFailGeneralDialogTitle;
      const message = (data.Message ?? '').includes('record already exists')
        ? this.submissionsConst.subSbraFailDuplicateMessage
        : data.Message.replace(`${AlertActions.SBRA_SUBMISSION_ERROR}:`, '');
      //popup to show SBRA submission failure
      this.dialogService.openDialog(
        title,
        message,
        this.submissionsConst.defaultButtonTxtCancel,
        this.submissionsConst.defaultButtonTxtConfirm,
        this.submissionsConst.defaultShowCancelButton,
      );
      this.subService.getSubmission(this.submissionViewItem.Id);
    }
  }

  private setStatusControlToSubmitted(controls: DynamicFormControl[]): void {
    const statusControl = controls.find((x) => x.propertyBinding === 'status');
    if (
      statusControl &&
      !statusControl.customOptions.selectOptions.includes(
        this.submissionsConst.statusSubmitted,
      )
    ) {
      statusControl.customOptions.selectOptions.push({
        value: 'Submitted',
        label: 'Submitted',
      });
    }
  }

  private removeSubmittedStatusOption(controls: DynamicFormControl[]): void {
    const statusControl = controls.find((x) => x.propertyBinding === 'status');
    if (!statusControl?.customOptions?.selectOptions) {
      return;
    }

    statusControl.customOptions.selectOptions =
      statusControl.customOptions.selectOptions.filter(
        (option: { value?: string }) =>
          option?.value !== this.submissionsConst.statusSubmitted,
      );
  }

  private lockControls(controls: DynamicFormControl[]): void {
    controls
      .map((i) => i.formControls[0])
      .forEach((i) => {
        i.formControl.disable({ onlySelf: true, emitEvent: false });
      });
  }

  private unlockControls(controls: DynamicFormControl[]): void {
    controls
      .filter(
        (i) =>
          i.customOptions?.isDisabled !== true &&
          i.controlType !== ControlType.READONLY,
      )
      .map((i) => i.formControls[0])
      .forEach((i) =>
        i.formControl.enable({ onlySelf: true, emitEvent: false }),
      );
  }

  private clearStatusSubmittedDate(controls: DynamicFormControl[]): void {
    const statusControl = controls.find((x) => x.propertyBinding === 'status');
    statusControl?.setHintText('');
  }

  private isSavedManualSubmission(): boolean {
    return (
      this.getStatus(this.submission) ===
      this.submissionsConst.statusSubmittedManually
    );
  }

  private shouldShowSubmittedDateHint(): boolean {
    if (this.subStatus === this.submissionsConst.statusSubmitted) {
      return true;
    }

    return (
      this.subStatus === this.submissionsConst.statusSubmittedManually &&
      this.canShowManualSubmittedDate
    );
  }

  private runRuleSet(formValues: FormGroup) {
    let controls = this.dynamicForm.getControls();
    let groups = this.dynamicForm.getGroups().filter((i) => i.label);
    this.subStatus = formValues.get('status').value;

    if (this.subStatus === this.submissionsConst.statusSubmitted) {
      this.setStatusControlToSubmitted(controls);
      this.setStatusControlSubmittedDate(controls);
      this.applyControlAndGroupVisibilityRules(formValues, controls, groups); // Do NOT lock controls here - they should remain unlocked until form is saved
      return;
    }

    if (this.shouldShowSubmittedDateHint()) {
      this.setStatusControlSubmittedDate(controls);
    } else {
      this.clearStatusSubmittedDate(controls);
    }

    this.removeSubmittedStatusOption(controls);

    this.formService
      .generateRuleSet(this.dynamicForm.rules, controls)
      .forEach((i) => {
        this.formService.applyRule(i, controls, groups);
      });

    //Custom Rules
    this.formService
      .generateRuleSet(
        this.customRulesService.getCustomRules(
          this.submission.FormStructureId,
          formValues,
        ),
        controls,
      )
      .forEach((i) => {
        this.formService.applyRule(i, controls, groups);
      });
    if (
      this.subStatus !== this.submissionsConst.statusReadyToSubmit &&
      this.subStatus !== this.submissionsConst.statusSubmittedManually
    ) {
      this.shouldSaveBeDisabled = false;
      controls
        .filter((x) => {
          const value = x.formControls[0].formControl.value;
          const isEmpty =
            value === null ||
            value === undefined ||
            `${value}`.trim().length === 0;
          return !x.hidden && x.customOptions?.isRequired && isEmpty;
        })
        .forEach((x) => {
          const formControl = x.formControls[0].formControl;
          // Strip the required validator (resetValidations re-adds it every
          // valueChanges cycle) in addition to clearing the error. Otherwise a
          // later revalidation - e.g. blurring the empty field - resurrects the
          // required error and the control renders red even though the
          // submission isn't in a submit-ready status. The validator is
          // re-applied by resetValidations and enforced via applyInvalidRule
          // once the status moves to Ready To Submit / Submitted Manually.
          formControl.removeValidators(Validators.required);
          formControl.setErrors(null);
          formControl.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
          });
        });
      return;
    }
    this.shouldSaveBeDisabled = true;
    //run MAKE_INVALID rule here based on if the control is required
    // Track empty-required controls ourselves: disabled controls are excluded
    // from Angular's form.valid computation and their validators don't run, so
    // form.valid alone would let a blank, disabled, required field (e.g.
    // orderNumber) pass. We must gate the save on this flag too.
    let hasEmptyRequiredControl = false;
    controls.forEach((i) => {
      if (!i.hidden) {
        const value = i.formControls[0].formControl.value;
        const isEmpty =
          value === null ||
          value === undefined ||
          `${value}`.trim().length === 0;
        if (i.customOptions.isRequired === true && isEmpty) {
          //mark as touched and invalid to trigger red highlight styling
          this.formService.applyInvalidRule(i);
          hasEmptyRequiredControl = true;
          // Admin-managed fields show a directed contact-support message.
          // resetValidations clears the message each cycle, so it self-heals
          // once the field is populated.
          if (this.submissionsConst.adminRequiredFields.has(i.propertyBinding)) {
            i.setError(this.submissionsConst.adminRequiredFieldError);
          }
        }

        // Angular skips validators on disabled controls, so run any registered
        // custom validators manually here.
        const formControl = i.formControls[0].formControl;
        if (formControl.disabled) {
          const customValidators = this.validatorRegistry.get(i.propertyBinding);
          for (const validator of customValidators) {
            const errors = validator(formControl);
            if (errors) {
              this.formService.applyInvalidRule(i);
              hasEmptyRequiredControl = true;
              const message = Object.values(errors).find((v) => typeof v === 'string');
              if (message) {
                i.setError(message);
              }
              break;
            }
          }
        }
      }
    });

    if (this.form.valid && !hasEmptyRequiredControl) {
      this.shouldSaveBeDisabled = false;
      return;
    }
  }
  setStatusControlSubmittedDate(controls: DynamicFormControl[]) {
    const plantNumber = this.submission.SubmissionValues.find(
      (x) => x.PropertyBindingKey === 'facilityNumber',
    )?.Value;

    const formattedString = Submission.IsNEEMPlant(plantNumber)
      ? 'NEEM Submission Recorded on {0}'
      : 'Submitted to SBRA on {0}';

    // For V2 submissions that were Manually Submitted, they didn't have SubmittedOn populated. In these cases,
    // we'll use the ModifiedOn date instead.
    const dateString =
      this.submission.SubmittedOn?.toString() ??
      this.submission.ModifiedOn?.toString();

    var control = controls.find((x) => x.propertyBinding === 'status');
    const formattedSubmittedOn = new Intl.DateTimeFormat('en-US', {
      month: 'numeric',
      day: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(dateString));

    const hintText = formattedString.replace('{0}', formattedSubmittedOn);
    control.setHintText(hintText);
  }

  private toSubmission(serializedSubmission: string): Submission {
    const submission = JSON.parse(serializedSubmission) as Submission;

    return {
      ...submission,
      SubmittedOn: submission.SubmittedOn
        ? new Date(submission.SubmittedOn)
        : null,
      ModifiedOn: submission.ModifiedOn
        ? new Date(submission.ModifiedOn)
        : null,
    };
  }
  private displayFormBanner(msg: string): void {
    this.formSaveBanner = msg;
    setTimeout(() => {
      this.formSaveBanner = '';
    }, 5000);
  }

  applyControlAndGroupVisibilityRules(
    formValues: FormGroup,
    controls: DynamicFormControl[],
    groups: DynamicFormGroup[],
  ) {
    const getRulesForVisibility = (rules: FormRule[]): FormRule[] => {
      const hasValidRuleAction = (x: FormRuleEffect) =>
        x.action === RuleAction.MAKE_HIDDEN ||
        x.action === RuleAction.MAKE_VISIBLE ||
        (x.applyTo === 'energyEfficiencyTypeBanner' &&
          x.action === RuleAction.SET_VALUE);
      const copyValidRule = (x: FormRule): FormRule => {
        const validEffects = x.effects.filter((y) => hasValidRuleAction(y));
        const newRule = new FormRule(x.condition, validEffects);
        newRule.appliedCondition = x.appliedCondition;
        newRule.runContinuously = x.runContinuously;
        return newRule;
      };
      return (rules ?? [])
        .filter((x) => x.effects.some((y) => hasValidRuleAction(y)))
        .map((x) => copyValidRule(x));
    };

    [
      ...this.formService.generateRuleSet(
        getRulesForVisibility(this.dynamicForm.rules),
        controls,
      ),
      ...this.formService.generateRuleSet(
        getRulesForVisibility(
          this.customRulesService.getCustomRules(
            this.submission.FormStructureId,
            formValues,
          ),
        ),
        controls,
      ),
    ].forEach((i) => this.formService.applyRule(i, controls, groups));
  }

  unsavedChanges() {
    const dialogRef = this.openDialog(
      this.submissionsConst.subDtlDialogTitle,
      this.submissionsConst.subDtlDialogMessage,
      this.submissionsConst.buttonTxtCancel,
      this.submissionsConst.buttonTxtConfirm,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.resetForm();
        this.closePanel();
      } else {
        this.unsavedChangesUpdate.emit(true);
      }
    });
  }

  resetForm() {
    this.form.reset();
    this.formService.clearRuleHashBlackList(); // need to clear this here as well to ensure rules are ran again
    this.canShowManualSubmittedDate = false;
    this.unsavedChangesUpdate.emit(false);
    this.subService.getSubmission(this.submissionViewItem.Id);
  }

  closePanel() {
    this.close.emit();
  }

  submissionFormSetup() {
    this.form = this.formBuilder.group([]);

    let sub = this.subService.submission$
      .pipe(
        switchMap((sub) => {
          return forkJoin([
            of(sub),
            this.formService.getForm(sub.FormStructureId),
          ]);
        }),
      )
      .subscribe(([submission, form]) => {
        this.isHydratingForm = true;
        this.submission = submission;

        this.subStatus =
          submission.SubmissionValues.find(
            (i) => i.PropertyBindingKey === 'status',
          )?.Value ?? '';
        this.canShowManualSubmittedDate = this.isSavedManualSubmission();

        this.calculateShouldShowDeleteButton();
        this.calculateShouldShowResetStatusButton();
        this.calculateShouldShowConvertV3ToV2Button();
        this.calculateShouldShowConvertV2ToV3Button();

        if (
          !this.dynamicForm ||
          this.FormStructureId !== submission.FormStructureId
        ) {
          Object.keys(this.form.controls).forEach((key) =>
            this.form.removeControl(key),
          );
          this.dynamicForm = form;
          this.dynamicForm.bindControls(this.form);
          this.FormStructureId = submission.FormStructureId;
        }
        let controls = this.dynamicForm.getControls();

        submission.SubmissionValues.forEach((i) => {
          let currentPropertyBindingKey =
            i.PropertyBindingKey.toLocaleLowerCase();

          let dynCtrl = controls.find(
            (x) =>
              x.propertyBinding.toLowerCase() === currentPropertyBindingKey,
          );

          if (
            currentPropertyBindingKey ===
            this.submissionsConst.homeSerialNumber.toLocaleLowerCase()
          ) {
            this.heading = i.Value;
          }
          dynCtrl?.setValue(i.Value, undefined, this.form);
        });

        this.runRuleSet(this.form);

        // Lock controls on initial load if status is already submitted (loaded from server)
        if (this.submissionsConst.submittedStatuses.includes(this.subStatus)) {
          this.lockControls(controls);
        }
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.unsavedChangesUpdate.emit(false);
        this.isHydratingForm = false;

        // Release the spinner held across a V2<->V3 conversion now that the
        // new form is fully rebuilt and hydrated, avoiding a blank-form flash.
        if (this.isConverting) {
          this.isConverting = false;
          this.spinnerService.hide();
        }
      });
    this._subscriptions.push(sub);

    sub = this.form.valueChanges.subscribe((i) => {
      if (this.isHydratingForm) {
        return;
      }

      this.canShowManualSubmittedDate = false;

      this.formService.resetValidations(
        this.dynamicForm.getControls(),
        this.dynamicForm.getGroups(),
        this.validatorRegistry,
      );
      this.subStatus = i.status;
      this.statusUpdate.emit(this.subStatus);
      this.runRuleSet(this.form);
      this.unsavedChangesUpdate.emit(true);
    });

    this._subscriptions.push(sub);
  }

  confirmDelete() {
    const dialogRef = this.openDialog(
      'Delete Submission',
      `Are you sure you want to delete this submission? This action cannot be undone.`,
      this.submissionsConst.buttonTxtCancel,
      this.submissionsConst.buttonTxtConfirm,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.subService
          .deleteSubmission(this.submissionViewItem.Id)
          .subscribe(() => {
            this.delete.emit();
          });
      }
    });
  }

  confirmResetStatus() {
    const dialogRef = this.openDialog(
      'Move Submission To In Progress',
      `Are you sure you want to move this submission to In Progress?`,
      this.submissionsConst.buttonTxtCancel,
      this.submissionsConst.buttonTxtConfirm,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.isHydratingForm = true;
        try {
          this.unlockControls(this.dynamicForm.getControls());
          this.form.patchValue(
            { status: this.submissionsConst.statusInProgress },
            { emitEvent: false },
          );
          this.submission.SubmittedOn = null;
          this.subStatus = this.submissionsConst.statusInProgress;
          this.canShowManualSubmittedDate = false;
          this.runRuleSet(this.form);
          this.form.markAsDirty();
        } finally {
          this.isHydratingForm = false;
        }

        this.unsavedChangesUpdate.emit(true);
        this.statusUpdate.emit(this.subStatus);
        this.updateButtonVisibilities();
      }
    });
  }

  confirmConvertV3ToV2() {
    this.confirmConvert({
      confirmationTitle:
        this.submissionsConst.convertV3ToV2ConfirmationDialogTitle,
      confirmationMessage:
        this.submissionsConst.convertV3ToV2ConfirmationDialogMessage,
      failedTitle: this.submissionsConst.convertV3ToV2FailedDialogTitle,
      failedMessage: this.submissionsConst.convertV3ToV2FailedDialogMessage,
      convert: (id) => this.subService.convertSbraSubmissionToV2(id),
      responseKey: 'ConvertSbraSubmissionToV2',
    });
  }

  confirmConvertV2ToV3() {
    this.confirmConvert({
      confirmationTitle:
        this.submissionsConst.convertV2ToV3ConfirmationDialogTitle,
      confirmationMessage:
        this.submissionsConst.convertV2ToV3ConfirmationDialogMessage,
      failedTitle: this.submissionsConst.convertV2ToV3FailedDialogTitle,
      failedMessage: this.submissionsConst.convertV2ToV3FailedDialogMessage,
      convert: (id) => this.subService.convertSbraSubmissionToV3(id),
      responseKey: 'ConvertSbraSubmissionToV3',
    });
  }

  private confirmConvert(config: {
    confirmationTitle: string;
    confirmationMessage: string;
    failedTitle: string;
    failedMessage: string;
    convert: (id: number) => Observable<any>;
    responseKey: string;
  }) {
    const dialogRef = this.openDialog(
      config.confirmationTitle,
      config.confirmationMessage,
      this.submissionsConst.buttonTxtCancel,
      this.submissionsConst.buttonTxtConfirm,
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      const showFailureDialog = () => {
        this.spinnerService.hide();
        this.openDialog(
          config.failedTitle,
          config.failedMessage,
          this.submissionsConst.defaultButtonTxtCancel,
          this.submissionsConst.defaultButtonTxtConfirm,
          this.submissionsConst.defaultShowCancelButton,
        );
      };

      this.spinnerService.show();
      config.convert(this.submissionViewItem.Id).subscribe({
        next: (value: any) => {
          const data: UpdateResult = value?.data?.[config.responseKey];

          if (!data?.Success) {
            showFailureDialog();
            return;
          }

          // Keep the spinner up (do NOT hide here) so the overlay covers the
          // blank form while we tear down and refetch. It is released once the
          // new form has been rebuilt in submissionFormSetup's subscription.
          this.formService.clearRuleHashBlackList();
          this._subscriptions.forEach((i) => i.unsubscribe());
          this._subscriptions = [];
          this.dynamicForm = null;
          this.unsavedChangesUpdate.emit(false);
          this.save.emit();
          this.isConverting = true;
          this.submissionFormSetup();
          this.subService.getSubmission(this.submissionViewItem.Id);
        },
        error: () => showFailureDialog(),
      });
    });
  }

  /***
   * opens the pop up dialog with the specified title, message, etc.
   * can handle the 'after close' as you wish, making it reusable for different circumstances
   * for example: Unsaved changes, SBRA success/failure, etc.
   **/
  openDialog(
    title: string,
    message: string,
    cancelText: string = '',
    confirmText: string = '',
    showCancel: boolean = true,
  ) {
    const dialogRef = this.dialogService.openDialog(
      title,
      message,
      cancelText,
      confirmText,
      showCancel,
    );

    return dialogRef;
  }
}
